import { LoadingDots, RadioField, RadioInput } from '@flos/react-ui';
import React, { useContext } from 'react';
import { ConfigContext } from '../../context/ConfigContext';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../helpers/templates';
import useService from '../../hooks/useService';
import { getConsentServiceRequestConfig } from '../../services/consent';
import useAppState from '../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { ElementValueHuman } from '../../types/element-values';
import { Consent as ConsentType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { ConsentData } from '../../types/services';
import { Status } from '../../types/status';
import { getValue } from '../values/Get';

const Consent = ({ values, caption, elementId, consentTypeId }: ConsentType) => {
    const formValues = useAppState((state) => state.formValues);
    const formConfig = useAppState((state) => state.formConfig);
    const setFormValue = useAppState((state) => state.setFormValue);
    const [fieldHasChangedAtLeastOnce, setFieldHasChangedAtLeastOnce] = React.useState(false);
    const appConfig = useContext(ConfigContext);

    const valueObj = Consent.getValueOrDefault(formValues, elementId);
    const isValid = Consent.isValidField(formConfig, formValues, elementId, fieldHasChangedAtLeastOnce);

    const { data: consentData, status } = useService<ConsentData>(
        getConsentServiceRequestConfig(appConfig, consentTypeId)
    );

    if (status === Status.LOADING) {
        return <LoadingDots centered={false} />;
    }

    return (
        <>
            {status === Status.ERROR && <p>Der er desværre sket en teknisk fejl</p>}
            {consentData && (
                <>
                    <div dangerouslySetInnerHTML={{ __html: consentData.text }} />
                    <br />
                    <RadioField
                        isValid={isValid}
                        label={caption}
                        id={elementId}
                        key={elementId}
                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                            const value = evt.target.value;
                            const label = values.find((item) => item.value === value)?.caption;
                            setFormValue(elementId, { label, value });
                            setFormValue('consentTypeId', consentTypeId);
                            setFieldHasChangedAtLeastOnce(true);
                        }}
                        value={valueObj.value}
                    >
                        {values.map((option, key) => (
                            <RadioInput key={key} label={option.caption} value={option.value} />
                        ))}
                    </RadioField>
                </>
            )}
        </>
    );
};

Consent.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (valueObject && valueObject.label !== '') {
        return true;
    }
    return false;
};

Consent.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

Consent.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Consent.getValue(formValues, elementId);
    if (value === undefined) {
        return { label: '', value: '' };
    }
    return value;
};

Consent.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasChangedAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasChangedAtLeastOnce) {
        return undefined;
    }

    const value = Consent.getValue(formValues, elementId);

    if (value === '' || value === undefined) {
        return undefined;
    }

    const isValid = Consent.isValid(formConfig, formValues, elementId);

    return isValid;
};

Consent.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as ConsentType;
    const { summaryCaption, values } = element;

    const value = getValue(formConfig, formValues, elementId);

    if (value === undefined) {
        return <></>;
    }

    const option = values.find((item) => item.value === value.value);

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={option?.caption} />;
};

Consent.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Consent.getValue(formValues, elementId);
    const value = valueObj.value;
    const labelX = valueObj.label;
    const valueId = {
        ConsentTypeId: formValues.consentTypeId,
        IsGiven: value,
        TimestampOfConsent: Math.round(Date.now() / 1000)
    };

    return getClaimFormSubmitValue('consent', elementId, label, labelX, valueId);
};

Consent.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Consent.getValue(formValues, elementId);
    const valueStr = JSON.stringify(valueObj);

    return valueStr;
};

export default Consent;
